import api from './api';
import { coordinatedRefresh } from './refreshCoordinator';
import type {
  LoginRequest,
  LoginResponse,
  RegisterRequest,
  RegisterResponse,
  User,
  AuthTokens,
  GoogleOAuthResponse,
  AppleOAuthResponse,
} from '@/types';

/**
 * OAuth providers status response
 */
export interface OAuthProvidersResponse {
  googleEnabled: boolean;
  appleEnabled: boolean;
  microsoftEnabled: boolean;
}

/**
 * Auth API Service
 * Handles authentication endpoints
 */
export const authApi = {
  /**
   * Login with email and password
   */
  login: async (data: LoginRequest): Promise<LoginResponse> => {
    // Backend accepts camelCase via APISchema
    return api.post<LoginResponse>('/auth/login', data);
  },

  /**
   * Register a new user
   * Returns user info and indicates email verification is required
   */
  register: async (data: RegisterRequest): Promise<RegisterResponse> => {
    // Backend accepts camelCase via APISchema
    return api.post<RegisterResponse>('/auth/register', data);
  },

  /**
   * Verify email with token from email link
   *
   * WP-SEC-27: verifying an unverified account wipes everything set up before
   * the proof, including any password. passwordSetupToken is then returned for
   * the "set your password" step (POST /auth/reset-password via ResetPasswordPage).
   *
   * @param token - Verification token from email
   * @param options - Optional request options including AbortSignal
   */
  verifyEmail: async (
    token: string,
    options?: { signal?: AbortSignal }
  ): Promise<{ message: string; passwordSetupToken?: string | null }> => {
    return api.post('/auth/verify-email', { token }, { signal: options?.signal });
  },

  /**
   * Resend email verification
   */
  resendVerificationEmail: async (email: string): Promise<{ message: string }> => {
    return api.post('/auth/verify-email/resend', { email });
  },

  /**
   * Logout - invalidate current session
   * @param options.allSessions - If true, revokes all user sessions (not just current)
   */
  logout: async (options?: { allSessions?: boolean }): Promise<void> => {
    return api.post('/auth/logout', {
      allSessions: options?.allSessions ?? false,
    });
  },

  /**
   * Refresh access token using httpOnly cookie
   * WP-AUTH-12: Refresh token is sent automatically via httpOnly cookie
   * WP-AUTH-REFRESH-DEDUP-1: Uses coordinated refresh for deduplication
   *
   * NOTE: Returns a partial AuthTokens (only accessToken guaranteed).
   * Cross-tab results only include accessToken - refreshToken is in httpOnly cookie.
   */
  refresh: async (): Promise<AuthTokens> => {
    const result = await coordinatedRefresh();
    // Return AuthTokens-compatible structure
    // refreshToken is in httpOnly cookie, not needed in response
    return {
      accessToken: result.accessToken,
      refreshToken: '', // Managed via httpOnly cookie
      expiresIn: 900, // 15 minutes default
      tokenType: 'Bearer',
    };
  },

  /**
   * Get current user
   * WP-GDPR-EXPORT-2: Transforms /auth/me response to match User type
   * Maps userId->id and includes auth info fields (hasPassword, oauthProviders, primaryAuthMethod)
   */
  me: async (): Promise<User> => {
    // CurrentUserInfoResponse fields from backend
    interface AuthMeResponse {
      userId: string;
      email: string;
      name: string;
      emailVerified: boolean;
      mfaEnabled: boolean;
      tenantId: string;
      tenantName: string;
      tenantPlan: string;
      role: User['role'];
      // WP-GDPR-EXPORT-2: Auth info fields
      hasPassword: boolean;
      oauthProviders: string[];
      primaryAuthMethod: string;
    }

    const response = await api.get<AuthMeResponse>('/auth/me');

    // Transform to User type
    return {
      id: response.userId,  // Map userId -> id
      email: response.email,
      name: response.name,
      tenantId: response.tenantId,
      // The endpoint now reports this. It used to be hardcoded to 'member'
      // with the comment "actual role comes from login response" - true until
      // the page is refreshed, which is exactly when login does NOT happen.
      // checkAuth() rebuilds the store's user from here on every load, so a
      // household owner was demoted to 'member' by a reload and lost every
      // owner-gated control (FinancialPlanningSettings, HouseholdSettings,
      // MemberList all gate on role === 'owner') until they logged in again.
      //
      // Kept tolerant of an older backend: if the field is absent, fall back to
      // the least privileged value rather than crashing or over-granting.
      role: response.role ?? 'member',
      createdAt: new Date().toISOString(),  // Placeholder - not in /auth/me
      updatedAt: new Date().toISOString(),  // Placeholder - not in /auth/me
      // WP-GDPR-EXPORT-2: Include auth info for OAuth detection
      hasPassword: response.hasPassword,
      oauthProviders: response.oauthProviders as User['oauthProviders'],
      primaryAuthMethod: response.primaryAuthMethod as User['primaryAuthMethod'],
    };
  },

  /**
   * Request password reset email
   */
  forgotPassword: async (email: string): Promise<void> => {
    return api.post('/auth/forgot-password', { email });
  },

  /**
   * Reset password with token
   */
  resetPassword: async (token: string, newPassword: string): Promise<void> => {
    return api.post('/auth/reset-password', { token, newPassword });
  },

  /**
   * Request magic link email
   */
  requestMagicLink: async (email: string): Promise<void> => {
    return api.post('/auth/magic-link', { email });
  },

  /**
   * Verify magic link token
   * @param token - Magic link token from email
   * @param deviceFingerprint - Optional device fingerprint for device tracking
   */
  verifyMagicLink: async (token: string, deviceFingerprint?: string): Promise<LoginResponse> => {
    return api.post<LoginResponse>('/auth/magic-link/verify', { token, deviceFingerprint });
  },

  /**
   * Verify MFA code (complete MFA challenge during login)
   * @param mfaToken - The MFA token from login
   * @param code - The TOTP, SMS, or recovery code
   * @param mfaMethod - The method used: 'totp', 'sms', 'recovery', or 'auto'
   * @param deviceFingerprint - Optional device fingerprint for device tracking
   * @param trustDevice - Whether to trust this device for future logins
   */
  verifyMfa: async (
    mfaToken: string,
    code: string,
    mfaMethod: 'totp' | 'sms' | 'recovery' | 'auto' = 'auto',
    deviceFingerprint?: string,
    trustDevice?: boolean
  ): Promise<LoginResponse> => {
    return api.post<LoginResponse>('/auth/mfa/challenge', {
      mfaToken,
      code,
      mfaMethod,
      deviceFingerprint,
      trustDevice,
    });
  },

  /**
   * Send SMS code for MFA challenge during login
   * @param mfaToken - The MFA token from login
   * @returns Phone last 4 digits and expiration
   */
  sendSmsForChallenge: async (
    mfaToken: string
  ): Promise<{ phoneLastFour: string; expiresInMinutes: number }> => {
    return api.post<{ phoneLastFour: string; expiresInMinutes: number }>(
      '/auth/mfa/challenge/send-sms',
      { mfaToken }
    );
  },

  /**
   * Login with Google OAuth
   * Sends the Google ID token to backend for validation and authentication
   * @param idToken - The Google ID token from Google Sign-In
   * @param termsAccepted - Optional boolean indicating terms acceptance (required for new user registration)
   * @param deviceFingerprint - Optional device fingerprint for MFA device trust (WP-AUTH-7)
   */
  loginWithGoogle: async (
    idToken: string,
    termsAccepted?: boolean,
    deviceFingerprint?: string
  ): Promise<GoogleOAuthResponse> => {
    return api.post<GoogleOAuthResponse>('/oauth/google', {
      idToken,
      termsAccepted,
      deviceFingerprint,
    });
  },

  /**
   * Login with Apple OAuth (Sign in with Apple)
   * Sends the Apple ID token to backend for validation and authentication
   * WP-FE-AUTH-1: Apple Sign In Web Frontend Integration
   *
   * @param idToken - The Apple ID token from Sign in with Apple
   * @param termsAccepted - Optional boolean indicating terms acceptance (required for new user registration)
   * @param deviceFingerprint - Optional device fingerprint for MFA device trust (WP-AUTH-7)
   * @param userFirstName - User's first name (only provided by Apple on first authorization)
   * @param userLastName - User's last name (only provided by Apple on first authorization)
   */
  loginWithApple: async (
    idToken: string,
    termsAccepted?: boolean,
    deviceFingerprint?: string,
    userFirstName?: string,
    userLastName?: string
  ): Promise<AppleOAuthResponse> => {
    return api.post<AppleOAuthResponse>('/oauth/apple', {
      idToken,
      termsAccepted,
      deviceFingerprint,
      userFirstName,
      userLastName,
    });
  },

  /**
   * Get available OAuth providers
   * Returns which OAuth providers are enabled on the backend
   */
  getOAuthProviders: async (): Promise<OAuthProvidersResponse> => {
    return api.get<OAuthProvidersResponse>('/oauth/providers');
  },

  /**
   * Get maintenance mode status
   * Returns whether the system is currently in maintenance mode (during deployments).
   * This endpoint is unauthenticated - designed for login page to check status.
   */
  getMaintenanceStatus: async (): Promise<{ maintenance: boolean }> => {
    return api.get<{ maintenance: boolean }>('/maintenance-status');
  },

  /**
   * Get Microsoft OAuth authorization URL
   * Initiates Microsoft OAuth flow by returning the authorization URL
   *
   * WP-AUTH-13: Consent data is passed as query parameters and stored in
   * OAuth state (Redis) so backend can retrieve it during callback.
   *
   * @param consentData - Optional consent data to embed in OAuth state
   */
  getMicrosoftAuthUrl: async (consentData?: {
    termsAccepted?: boolean;
    privacyAccepted?: boolean;
    gdprConsent?: boolean;
    marketingConsent?: boolean;
    consentTimestamp?: string;
  }): Promise<string> => {
    // Build query params for consent data
    const params = new URLSearchParams();
    if (consentData?.termsAccepted) params.append('terms_accepted', 'true');
    if (consentData?.privacyAccepted) params.append('privacy_accepted', 'true');
    if (consentData?.gdprConsent) params.append('gdpr_consent', 'true');
    if (consentData?.marketingConsent) params.append('marketing_consent', 'true');
    if (consentData?.consentTimestamp) params.append('consent_timestamp', consentData.consentTimestamp);

    const queryString = params.toString();
    const url = queryString
      ? `/oauth/microsoft/authorize?${queryString}`
      : '/oauth/microsoft/authorize';

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const response = await api.get<any>(url);
    return response.authorizationUrl ?? response.authorization_url;
  },
};

/**
 * Response from tenant switch endpoint
 */
export interface TenantSwitchResponse {
  accessToken: string;
  refreshToken: string;
  tokenType: string;
  tenantId: string;
  tenantName: string;
  role: 'owner' | 'member';
}

/**
 * Switch to a different tenant (household).
 *
 * Issues new JWT tokens with the updated tenant context.
 * Caller must update auth context and invalidate cached data.
 *
 * @param tenantId - UUID of target tenant to switch to
 * @returns New tokens and tenant info
 * @throws Error if not a member of target tenant (403) or tenant not found (404)
 */
export async function switchTenant(tenantId: string): Promise<TenantSwitchResponse> {
  const response = await api.post<TenantSwitchResponse>('/households/tenant/switch', {
    tenantId,
  });
  return response;
}

export default authApi;
